import * as React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import { AutoSizer, List } from 'react-virtualized'

import { IAttachment, LocationType } from '~/client/graph'
import CompanyProfilePreview from '~/client/src/shared/components/CompanyProfilePreview/CompanyProfilePreview'
import {
  getLocationParentNames,
  LocationReference,
} from '~/client/src/shared/components/ObjectReference/LocationReference'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/client/src/shared/components/shadcn-ui/popover'
import SitemapAttributeTag from '~/client/src/shared/components/SitemapAttributeTag/SitemapAttributeTag'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import User from '~/client/src/shared/models/User'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import InitialState from '~/client/src/shared/stores/InitialState'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import AttachmentTag from '../../shared/components/AttachmentTag/AttachmentTag'
import Material from '../../shared/models/Material'

export interface ICellEntity {
  id: string
  name?: string
}

export enum TagCellItemKeyEnum {
  COMPANY = 'Company',
  USER = 'User',
  LOCATION = 'Location',
  EQUIPMENT = 'Equipment',
  ATTACHMENT = 'Attachment',
  MATERIAL = 'Material',
  MATERIAL_RESOURCE = 'MaterialResource',
}

interface IProps {
  items: ICellEntity[]

  isBreadcrumb?: boolean
  renderItemKey?: TagCellItemKeyEnum

  getXItemsLabel?(itemsCount: number): string
  renderItem?(item: ICellEntity, title?: string): JSX.Element
  className?: string
  containerStyle?: React.CSSProperties

  state?: InitialState
  tagsStore?: TagsStore
}

const ROW_HEIGHT = 50
const MODAL_HEIGHT_RATIO = 0.4
const OVERSCAN_ROW_COUNT = 10

const renderLocationTag = (
  locationObj: LocationBase,
  title?: string,
  onClick?: (id: string) => void,
  parentNames?: string[],
): JSX.Element => {
  const locationName = title || locationObj.name

  const name = <span title={locationName}>{locationName}</span>
  const reference = parentNames ? (
    <LocationReference
      iconName={locationObj.iconName}
      color={locationObj.color}
      variant="plain"
      size="dense"
      parentNames={parentNames}
      resourceCode={locationObj.stxResourceId}
    >
      {name}
    </LocationReference>
  ) : (
    <LocationReference
      iconName={locationObj.iconName}
      color={locationObj.color}
      variant="plain"
      size="dense"
    >
      {name}
    </LocationReference>
  )

  if (!onClick) return reference

  // Clickable, but still plain: the cell opens the equipment's card, and the name reads as
  // the row's own text rather than as a link — as it did under the tag this replaced.
  const activate = (event: React.SyntheticEvent) => {
    event.stopPropagation()
    onClick(locationObj.id)
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className="tw:flex tw:min-w-0 tw:max-w-full tw:cursor-pointer"
      onClick={activate}
      onKeyDown={event => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        activate(event)
      }}
    >
      {reference}
    </span>
  )
}

const renderCompanyPreview = (company: Company): JSX.Element => (
  <CompanyProfilePreview
    company={company}
    shouldShowCompanyType
    isMenuHidden
    isPhoneHidden
  />
)

const renderAttachmentTag = (attachment: IAttachment): JSX.Element => (
  <AttachmentTag attachment={attachment} />
)

const renderUserPreview = (user: User): JSX.Element => (
  <UserProfilePreview user={user} />
)

const renderMaterialTag = (material: Material): JSX.Element => {
  const materialName = material.productName
  return (
    <SitemapAttributeTag
      shouldShowAsTag={true}
      contentContainerClassName="text-ellipsis py2"
    >
      <span title={materialName} className="text large">
        {materialName}
      </span>
    </SitemapAttributeTag>
  )
}

const renderMaterialResourceTag = (record: IResourceRecord): JSX.Element => {
  const label = record.stxResourceId
    ? `${record.stxResourceId}: ${record.name}`
    : record.name
  return (
    <SitemapAttributeTag
      shouldShowAsTag={true}
      contentContainerClassName="text-ellipsis py2"
    >
      <span title={label} className="text large">
        {label}
      </span>
    </SitemapAttributeTag>
  )
}

const defaultItemLabelMap = {
  [TagCellItemKeyEnum.COMPANY]: Localization.translator.xCompanies,
  [TagCellItemKeyEnum.USER]: Localization.translator.xUsers,
  [TagCellItemKeyEnum.LOCATION]: Localization.translator.xLocations,
  [TagCellItemKeyEnum.EQUIPMENT]: (x: number) =>
    `${x} ${Localization.translator.equipment}`,
  [TagCellItemKeyEnum.ATTACHMENT]: Localization.translator.xAttachments,
  [TagCellItemKeyEnum.MATERIAL]: Localization.translator.xMaterials,
  [TagCellItemKeyEnum.MATERIAL_RESOURCE]: Localization.translator.xMaterials,
}
const defaultItemRendererMap = {
  [TagCellItemKeyEnum.COMPANY]: renderCompanyPreview,
  [TagCellItemKeyEnum.USER]: renderUserPreview,
  [TagCellItemKeyEnum.ATTACHMENT]: renderAttachmentTag,
  [TagCellItemKeyEnum.MATERIAL]: renderMaterialTag,
  [TagCellItemKeyEnum.MATERIAL_RESOURCE]: renderMaterialResourceTag,
}

@inject('state', 'tagsStore')
@observer
export default class ListTagCell extends React.Component<IProps> {
  public render() {
    const { items, isBreadcrumb, className, containerStyle } = this.props

    if (!items?.length) {
      return <div className="pt20 pb7">{NO_VALUE}</div>
    }

    if (isBreadcrumb || items.length === 1) {
      const itemAsBreadcrumb =
        isBreadcrumb && items.map(l => l?.name).join(' > ')

      return (
        <div
          className={classList({
            'text-ellipsis': true,
            [className]: !!className,
          })}
        >
          {this.renderItem(items[0], itemAsBreadcrumb)}
        </div>
      )
    }

    return (
      <div
        className={classList({
          [className]: !!className,
        })}
        style={containerStyle}
        onClick={this.stopPropagation}
      >
        <Popover>
          <PopoverTrigger
            nativeButton={false}
            render={
              <div className="tw:flex tw:items-center primary50-color-hover pointer h48 no-select underline-hover" />
            }
          >
            <div className="tw:flex-1 text-ellipsis">{this.xItemsLabel}</div>
            <ChevronDownIcon
              size={11}
              className="ml5 tw:text-muted-foreground"
            />
          </PopoverTrigger>
          <PopoverContent side="bottom">{this.modalContent}</PopoverContent>
        </Popover>
      </div>
    )
  }

  private get modalContent(): JSX.Element {
    const { items } = this.props

    return (
      <div className="col no-outline-container">
        <div className="virtualized-list-smart-wrapper">
          <AutoSizer disableHeight>
            {({ width }) => (
              <List
                data={items}
                width={width}
                height={Math.min(
                  window.innerHeight * MODAL_HEIGHT_RATIO,
                  ROW_HEIGHT * items.length,
                )}
                rowHeight={ROW_HEIGHT}
                rowCount={items.length}
                rowRenderer={this.renderRow}
                scrollToAlignment="start"
                overscanRowCount={OVERSCAN_ROW_COUNT}
              />
            )}
          </AutoSizer>
        </div>
      </div>
    )
  }

  private renderRow = ({ key, style, index }: any): JSX.Element => {
    const item = this.props.items[index]

    if (!item) {
      return null
    }

    return (
      <div key={key} style={style} className="row">
        <div className="ml15 mr10 text large bold no-grow">{index + 1}.</div>
        <div className="row text-ellipsis mr10">{this.renderItem(item)}</div>
      </div>
    )
  }

  private renderItem = (item: ICellEntity, title?: string): JSX.Element => {
    if (!item) {
      return null
    }

    const { renderItem, renderItemKey } = this.props

    if (renderItem) {
      return renderItem(item, title)
    }

    if (
      renderItemKey === TagCellItemKeyEnum.EQUIPMENT ||
      renderItemKey === TagCellItemKeyEnum.LOCATION
    ) {
      const location = item as LocationBase
      const onClick =
        location?.type === LocationType.OffloadingEquipment &&
        !location.isDeleted
          ? this.openResourceCard
          : undefined
      // `title` is the pre-joined breadcrumb the Deliveries rows pass instead of a name —
      // it already spells the path out, so don't repeat it on a second line.
      const parentNames = title
        ? undefined
        : getLocationParentNames(
            location,
            this.props.tagsStore.tagStoreByTagTypeMap,
          )
      return renderLocationTag(location, title, onClick, parentNames)
    }

    return defaultItemRendererMap[renderItemKey]?.(item as any)
  }

  private openResourceCard = (id: string) => {
    const { state, tagsStore } = this.props
    const eq = tagsStore.getTagById(id) as LocationBase
    if (!eq || eq.isDeleted) return
    state.setResourceDisplayedOnGlobalCard(
      id,
      ResourceTypeId.OFFLOADING_EQUIPMENT,
    )
  }

  private get xItemsLabel(): string {
    const { items, renderItemKey, getXItemsLabel } = this.props

    if (getXItemsLabel) {
      return getXItemsLabel(items.length)
    }

    return defaultItemLabelMap[renderItemKey](items.length)
  }

  private stopPropagation = (event: React.SyntheticEvent) => {
    event?.stopPropagation()
  }
}
